// MIDAS V3 — Per-thread answer-pipeline OPT-OUT pills (MODEL-PICKER-PIPELINE)
//
// COMPOSER-MENU rework (2026-07-15): this control no longer sits inline in the
// composer input row — it renders inside ComposerMenuV3 (the ☰ hamburger
// popover), under the "Pipeline" section header. The bot flipped its server
// default so an OMITTED `pipeline_override` runs v3 (agentic diagnosis), so
// the picker's job INVERTED: the options are the explicit OPT-OUTS "v1"
// (classic single-shot) and "v2" (pipeline v2). "v3" is no longer an explicit
// option — nothing selected = field omitted = v3 default. The section
// header/helper copy in ComposerMenuV3 makes the default visible.
//
// Interaction is unchanged from the old v2/v3 selector: two mutually-exclusive
// deselectable pills. Selecting one stamps `pipeline_override: "<id>"` on
// EVERY outgoing turn of this thread; clicking the active pill again clears
// it, and selecting the other switches. When neither is selected the field is
// OMITTED entirely (never ""). State lives in
// midasV3.pipelineOverrideByThread keyed by the SAME `threadKey` as the model
// picker (activeThreadId ?? "draft" for the primary; col.threadId per column)
// with identical localStorage persistence + draft-key migration — a stale
// persisted "v3" from the pre-flip build fails the PIPELINE_OVERRIDE_IDS
// hydration/dispatch guards and reads as no-selection here.
//
// The pipeline ids ("v1"/"v2") are bot-internal product-ish names, so the
// pill labels are literal + untranslated (like the model product names).
// Their descriptive meaning lives in the translated tooltips. NOTE the naming
// collision: the bot's pipeline "v1"/"v2"/"v3" is BOT-INTERNAL versioning,
// unrelated to our V1/V2/V3 chat surfaces — this control lives in the V3 UI.
//
// `.cc` ONLY (isCcDevSurface): renders null off-`.cc` — belt+braces, the
// hosting ComposerMenuV3 is itself `.cc`-gated. UX-only gate; the bot honors
// the field only behind its own flag + Origin allowlist anyway.
//
// Visual language mirrors the ModelPickerV3 trigger: 30px tall, radius 7,
// mono 11.5, transparent → accent-soft on hover, accent text + soft fill when
// selected, plus a small state pip so the active option reads at a glance.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useState } from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  PIPELINE_OVERRIDE_IDS,
  PipelineOverrideId,
  setThreadPipeline,
} from "../../midasV3Slice";
import { isCcDevSurface } from "./ccSurface";

export interface PipelineToggleV3Props {
  /** Per-thread selection key — SAME key the model picker uses:
   *  activeThreadId ?? "draft" (primary) or the attached column's threadId. */
  threadKey: string;
  disabled?: boolean;
}

/** Per-option tooltip translation keys (descriptive meaning lives here). */
const TOOLTIP_KEY: Record<PipelineOverrideId, string> = {
  v1: "midas.v3.pipelineToggle.tooltipV1",
  v2: "midas.v3.pipelineToggle.tooltipV2",
};

const TOOLTIP_DEFAULT: Record<PipelineOverrideId, string> = {
  v1: "Pipeline v1 — classic single-shot answer (bot-side). Applies to every message in this thread",
  v2: "Pipeline v2 (bot-side). Applies to every message in this thread",
};

const PipelineToggleV3: React.FC<PipelineToggleV3Props> = ({
  threadKey,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const [hoveredId, setHoveredId] = useState<PipelineOverrideId | null>(null);

  const stored = useAppSelector(
    (s) => s.midasV3.pipelineOverrideByThread[threadKey]
  ) as string | undefined;
  // Stale-state guard (mirrors the dispatch-time guard in chatBotSlice): a
  // persisted "v3" from the pre-flip live build is NOT in the selectable set
  // → render as no-selection/default rather than a phantom-active pill.
  const selected: PipelineOverrideId | undefined =
    typeof stored === "string" &&
    (PIPELINE_OVERRIDE_IDS as readonly string[]).includes(stored)
      ? (stored as PipelineOverrideId)
      : undefined;

  // `.cc`-only surface: render NOTHING off-`.cc` — no reserved space. All
  // hooks above run unconditionally so the hook order is stable.
  if (!isCcDevSurface()) return null;

  const groupAria = translate("midas.v3.pipelineToggle.groupAria", {
    defaultValue: "Answer pipeline for this thread",
  }) as string;

  const tooltipFor = (id: PipelineOverrideId): string =>
    translate(TOOLTIP_KEY[id], { defaultValue: TOOLTIP_DEFAULT[id] }) as string;

  return (
    <div
      role="group"
      aria-label={groupAria}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        flexShrink: 0,
      }}
    >
      {PIPELINE_OVERRIDE_IDS.map((id) => {
        const isOn = selected === id;
        const active = hoveredId === id || isOn;
        const tooltip = tooltipFor(id);
        return (
          <button
            key={id}
            type="button"
            role="switch"
            aria-checked={isOn}
            aria-label={tooltip}
            title={tooltip}
            disabled={disabled}
            onClick={() =>
              // Click the active option again → back to default (null);
              // otherwise select it (overwrites the other — the options are
              // mutually exclusive).
              dispatch(
                setThreadPipeline({ threadKey, pipeline: isOn ? null : id })
              )
            }
            onMouseEnter={() => setHoveredId(id)}
            onMouseLeave={() => setHoveredId(null)}
            style={{
              height: 30,
              padding: "0 8px",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              flexShrink: 0,
              border: "none",
              borderRadius: 7,
              background: active ? "var(--accent-soft)" : "transparent",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.5 : 1,
              transition: "background 0.12s ease, color 0.12s ease",
              fontFamily: "inherit",
            }}
          >
            {/* State pip — filled accent when selected, hollow otherwise, so
                the active option reads at a glance without relying on hover. */}
            <span
              aria-hidden="true"
              style={{
                width: 7,
                height: 7,
                flexShrink: 0,
                borderRadius: "50%",
                background: isOn ? "var(--accent)" : "transparent",
                border:
                  "1.5px solid " + (isOn ? "var(--accent)" : "var(--faint)"),
                transition: "background 0.12s ease, border-color 0.12s ease",
              }}
            />
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11.5,
                letterSpacing: 0,
                color: isOn
                  ? "var(--accent)"
                  : active
                  ? "var(--accent)"
                  : "var(--muted)",
                transition: "color 0.12s ease",
                whiteSpace: "nowrap",
              }}
            >
              {id}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default PipelineToggleV3;
